import { DEVICE_CHECK_MAX_AGE_MS } from "../constants/device";

/**
 * 기기 등록 판정 결과 캐시 (대화 단위)
 *
 * 뷰어(d라이브러리)가 "뷰어를 열 때 1회" 판정하고 페이지를 넘길 때는
 * 호출하지 않는 것과 동일한 원칙을 채팅에 적용한다.
 * 같은 대화를 이어가는 동안에는 판정 결과를 재사용하고,
 * 대화가 바뀌거나 안전 상한을 넘기면 다시 판정한다.
 *
 * 성공한 판정만 저장한다. 초과(409)/인증 만료(401)는 저장하지 않으므로
 * 기기 해제 후 "다시 시도"는 항상 서버에 다시 물어본다.
 */

/** 아직 그룹이 만들어지지 않은 새 대화의 키 */
export const NEW_CONVERSATION_KEY = "new";

/**
 * 대화 단위 식별자를 만든다.
 * 튜터 그룹 ID와 소크라테스 면접 ID가 숫자로 겹칠 수 있어 모드를 접두사로 붙인다.
 */
export const getConversationKey = (
  mode: string,
  id?: string | null
): string => `${mode}:${id || NEW_CONVERSATION_KEY}`;

interface DeviceCheckCache {
  userId: string | null;
  conversationKey: string;
  checkedAt: number;
}

let cache: DeviceCheckCache | null = null;

/**
 * 이 대화에서 이미 판정을 통과했는지 확인한다.
 * true면 register 호출을 생략해도 된다.
 */
export const isDeviceCheckValid = (
  userId: string | null,
  conversationKey: string
): boolean => {
  // 상한이 0이면 캐시 비활성화 (항상 판정)
  if (DEVICE_CHECK_MAX_AGE_MS <= 0) return false;
  if (!cache) return false;

  if (cache.userId !== userId) return false;
  if (cache.conversationKey !== conversationKey) return false;

  return Date.now() - cache.checkedAt < DEVICE_CHECK_MAX_AGE_MS;
};

/** 판정 성공을 기록한다 */
export const markDeviceChecked = (
  userId: string | null,
  conversationKey: string
): void => {
  cache = { userId, conversationKey, checkedAt: Date.now() };
};

/**
 * 새 대화의 임시 키를 실제 대화 ID로 승격한다.
 *
 * 새 대화의 첫 메시지는 그룹이 만들어지기 전에 판정하므로 "new" 키로 기록된다.
 * 그룹 생성 후 승격해두지 않으면 두 번째 메시지에서 대화가 바뀐 것으로 오인해
 * register가 한 번 더 나간다.
 */
export const promoteConversationKey = (conversationKey: string): void => {
  if (!cache) return;

  cache = { ...cache, conversationKey };
};

/** 캐시 무효화 (로그아웃, 탭 복귀 등) */
export const clearDeviceCheckCache = (): void => {
  cache = null;
};
